// src/components/PreviewPage.jsx
import { useState, useRef, useEffect } from "react";
import { Document, Page } from "react-pdf";
import { useParams } from "react-router-dom";
import AddRequestFieldModal from "../components/AddRequestFieldModal";
import SelectedFieldPanel from "../components/SelectedFieldPanel";
import Modal from "../components/Modal";
import {
  Button,
} from "flowbite-react";
import { authFetch } from "../utils/authFetch";

export default function PreviewPage() {
  const [numPages, setNumPages] = useState(0);
  const [pageSizes, setPageSizes] = useState({});
  const [selectedField, setSelectedField] = useState(null);

  // Request-body builder state
  const [requestFields, setRequestFields] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [newFieldName, setNewFieldName] = useState("");
  const [newFieldType, setNewFieldType] = useState("string");
  const [newFieldLabel, setNewFieldLabel] = useState("");
  const [isList, setIsList] = useState(false);

  // Preset state
  const [showPresetModal, setShowPresetModal] = useState(false);
  const [presetName, setPresetName] = useState("");
  const [presets, setPresets] = useState([]);
  const [showImportModal, setShowImportModal] = useState(false);

  // dynamic PDF width
  const previewRef = useRef(null);
  const [previewWidth, setPreviewWidth] = useState(0);

  const { uploadId } = useParams();
  const [fields, setFields] = useState({});
  const [mapping, setMapping] = useState({});
  const [file, setFile] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [fieldsMap, setFieldsMap] = useState({});
  const [defaultFontSize, setDefaultFontSize] = useState(null);

  // 1️⃣ Fetch your initial data and flip `loaded` when you have it
  useEffect(() => {
    authFetch(`${process.env.REACT_APP_BACKEND_BASE_URL}/api/pdf-generator/${uploadId}/`)
      .then((r) => r.json())
      .then((json) => {
        setFields      (json.fields);
        setRequestFields(json.request_body);
        setMapping     (json.mapping);
        setFieldsMap   (json.fields_map);
        setFile        (process.env.REACT_APP_BACKEND_BASE_URL + json.pdf_file);
        setLoaded(true);
      })
      .catch(console.error);

    authFetch(`${process.env.REACT_APP_BACKEND_BASE_URL}/api/pdf-generator/config/font-size/`)
      .then((r) => r.json())
      .then((json) => setDefaultFontSize(json.font_size))
      .catch(console.error);
  }, [uploadId]);

  // 2️⃣ Only sync _after_ you have loaded the real server data
  useEffect(() => {
    if (!loaded) return;             // ← don’t fire until you’ve loaded from GET

    authFetch(`${process.env.REACT_APP_BACKEND_BASE_URL}/api/pdf-generator/${uploadId}/`, {
      method:  "PATCH",
      headers: { "Content-Type": "application/json" },
      body:    JSON.stringify({ mapping, request_body: requestFields }),
    })
      .then((r) => {
        if (!r.ok) throw new Error(r.statusText);
        return r.json();
      })
      .then((json) => {
        console.log("Saved!", json);
        setFieldsMap(json.fields_map);
      })
      .catch((err) => console.error("Sync failed:", err));
  }, [mapping, requestFields, uploadId, loaded]);

  useEffect(() => {
    const updateWidth = () => {
      if (previewRef.current) {
        setPreviewWidth(previewRef.current.clientWidth);
      }
    };
    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, []);

  // download filled PDF
  const downloadFilled = async () => {
    const res = await authFetch(
      `${process.env.REACT_APP_BACKEND_BASE_URL}/api/pdf-generator/fill/`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: uploadId, mappings: mapping }),
      }
    );
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "filled.pdf";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  // capture page dims
  const onPageLoadSuccess = (pageProxy, index) => {
    const vp = pageProxy.getViewport({ scale: 1 });
    setPageSizes((p) => ({
      ...p,
      [index]: { widthPt: vp.width, heightPt: vp.height },
    }));
  };

  // Add new field to requestFields
  const handleAddField = () => {
    if (!newFieldName.trim()) return;
    setRequestFields((f) => [
      ...f,
      { name: newFieldName.trim(), type: newFieldType, isList: isList, label: newFieldLabel.trim() },
    ]);
    setNewFieldName("");
    setNewFieldType("string");
    setIsList(false);
    setShowModal(false);
  };

  // Remove a request field by index
  const handleRemoveField = (idx) => {
    if (window.confirm("Are you sure you want to remove this field?")) {    
      setRequestFields((f) => f.filter((_, i) => i !== idx));
    }
  };

  // Load presets list
  const loadPresets = async () => {
    try {
      const response = await authFetch(`${process.env.REACT_APP_BACKEND_BASE_URL}/api/pdf-generator/presets/`);
      const data = await response.json();
      setPresets(data);
      setShowImportModal(true);
    } catch (error) {
      console.error("Failed to load presets:", error);
      alert("Failed to load presets");
    }
  };

  // Import a preset
  const handleImportPreset = async (presetName) => {
    try {
      const response = await authFetch(`${process.env.REACT_APP_BACKEND_BASE_URL}/api/pdf-generator/presets/?name=${encodeURIComponent(presetName)}`);
      const data = await response.json();
      setRequestFields(data.fields || []);
      setShowImportModal(false);
    } catch (error) {
      console.error("Failed to import preset:", error);
      alert("Failed to import preset");
    }
  };

  // Save preset
  const handleSavePreset = async () => {
    if (!presetName.trim()) {
      alert("Please enter a preset name");
      return;
    }
    try {
      const response = await authFetch(`${process.env.REACT_APP_BACKEND_BASE_URL}/api/pdf-generator/presets/save/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: presetName, fields: requestFields }),
      });
      if (!response.ok) throw new Error(response.statusText);
      alert("Preset saved successfully!");
      setPresetName("");
      setShowPresetModal(false);
    } catch (error) {
      console.error("Failed to save preset:", error);
      alert("Failed to save preset");
    }
  };

  function Sidebar() {
    return (
      <div className="w-72 h-full overflow-y-auto flex-shrink-0 space-y-6">
        {/* --- existing map & reorder UI --- */}
        <div>
          <button
            onClick={downloadFilled}
            className="mt-4 w-full px-4 py-2 bg-green-600 text-white rounded hidden"
          >
            Download Filled PDF
          </button>
        </div>

        {/* --- NEW request-body builder UI --- */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-lg font-semibold mb-2">Request Body</h3>
            <Button
              color="light"
              onClick={() => setShowModal(true)}
            >
              + Add Field
            </Button>
          </div>
          
          {/* Preset buttons */}
          <div className="flex gap-2 mb-4">
            <Button
              color="gray"
              size="sm"
              onClick={loadPresets}
              className="flex-1"
            >
              Import Presets
            </Button>
            <Button
              color="gray"
              size="sm"
              onClick={() => setShowPresetModal(true)}
              className="flex-1"
            >
              Save Preset
            </Button>
          </div>

          {requestFields.length === 0 && (
            <p className="text-sm text-gray-500">No fields added.</p>
          )}
          <ul className="space-y-2 mb-4 mt-4">
            {requestFields.map((f, idx) => (
              <li
                key={idx}
                className="flex items-center justify-between bg-gray-100 px-3 py-2 rounded"
              >
                <span className="font-medium">{f.name}</span>
                <span className="text-sm text-gray-600">{f.isList ? "List of " + f.label + "s" : f.label}</span>
                <button
                  onClick={() => handleRemoveField(idx)}
                  className="text-red-600 hover:text-red-800 text-xs"
                >
                  ❌
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Add-Field Modal */}
      <AddRequestFieldModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        newFieldName={newFieldName}
        setNewFieldName={setNewFieldName}
        newFieldType={newFieldType}
        setNewFieldType={setNewFieldType}
        isList={isList}
        fieldLabel={ newFieldLabel}
        setFieldLabel={setNewFieldLabel}
        setIsList={setIsList}
        onAddField={handleAddField}
      />

      {/* Save Preset Modal */}
      <Modal
        isOpen={showPresetModal}
        onClose={() => {
          setShowPresetModal(false);
          setPresetName("");
        }}
        title="Save Preset"
        footer={
          <div className="flex gap-2 justify-end">
            <Button
              color="gray"
              onClick={() => {
                setShowPresetModal(false);
                setPresetName("");
              }}
            >
              Cancel
            </Button>
            <Button
              color="blue"
              onClick={handleSavePreset}
            >
              Save
            </Button>
          </div>
        }
      >
        <input
          type="text"
          value={presetName}
          onChange={(e) => setPresetName(e.target.value)}
          placeholder="Enter preset name"
          className="w-full border border-gray-300 rounded px-3 py-2"
        />
      </Modal>

      {/* Import Preset Modal */}
      <Modal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        title="Import Preset"
        footer={
          <div className="flex justify-end">
            <Button
              color="gray"
              onClick={() => setShowImportModal(false)}
            >
              Close
            </Button>
          </div>
        }
      >
        {presets.length === 0 ? (
          <p className="text-gray-500 text-center py-4">No presets available</p>
        ) : (
          <ul className="space-y-2">
            {presets.map((preset, idx) => (
              <li
                key={idx}
                className="flex items-center justify-between bg-gray-100 px-4 py-3 rounded hover:bg-gray-200 cursor-pointer"
                onClick={() => handleImportPreset(preset.name)}
              >
                <span className="font-medium">{preset.name}</span>
                <span className="text-sm text-gray-600">
                  {preset.fields?.length || 0} fields
                </span>
              </li>
            ))}
          </ul>
        )}
      </Modal>

      {/* Main layout */}
      <div className="flex w-full h-screen gap-6 p-5 overflow-hidden">
        <Sidebar />

        {/* PDF Preview */}
        <div
          ref={previewRef}
          className="relative flex-1 border border-gray-300 overflow-y-auto"
        >
          <Document
            file={file}
            onLoadSuccess={({ numPages }) => setNumPages(numPages)}
            loading={<></>}
            noData={<></>}
            error={<></>}
          >
            {Array.from({ length: numPages }, (_, i) => (
              <Page
                key={i}
                pageNumber={i + 1}
                width={previewWidth}
                onLoadSuccess={(page) => onPageLoadSuccess(page, i)}
                renderTextLayer={false}
                renderAnnotationLayer={false}
              />
            ))}
          </Document>

          {Object.entries(fields).map(([name, f]) => {
            const { rect, page } = f;
            const size = pageSizes[page];
            if (!size) return null;
            const scale = previewWidth / size.widthPt;
            const pageH = size.heightPt * scale;
            const yOffset = page * pageH;
            const [x1, y1, x2, y2] = rect;
            const isSelected = selectedField === name;
            return (
              <div
                key={name}
                onClick={() => setSelectedField(name)}
                style={{
                  position: "absolute",
                  left: x1 * scale,
                  top: yOffset + (size.heightPt - y2) * scale,
                  width: (x2 - x1) * scale,
                  height: (y2 - y1) * scale,
                  border: `${isSelected ? "5px solid #e53e3e" : "3px dashed #1890ff"}`,
                  cursor: "pointer",
                  pointerEvents: "auto",
                }}
              />
            );
          })}
        </div>
        <SelectedFieldPanel
          selectedField={selectedField}
          mapping={mapping}
          setMapping={setMapping}
          requestFields={requestFields}
          fieldsMap={fieldsMap}
          defaultFontSize={defaultFontSize}
        />
      </div>
    </>
  );
}
